import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { employeesApi, impersonationApi } from '../services/api';
import { setImpersonationToken, clearImpersonationToken } from '../utils/api';
import LoadingSpinner from '../components/common/LoadingSpinner';

const AuthContext = createContext();

// Function to generate auth URLs based on current host
export const getAuthUrls = () => {
  const origin = window.location.origin;
  return {
    loginUrl: `${origin}/api/auth/login`,
    logoutUrl: `${origin}/api/auth/logout`
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authInProgress, setAuthInProgress] = useState(false);
  const [impersonationSession, setImpersonationSession] = useState(null);
  const [impersonationLoading, setImpersonationLoading] = useState(false);
  const [impersonationChangeKey, setImpersonationChangeKey] = useState(0);

  const login = useCallback(() => {
    setAuthInProgress(true);
    const { loginUrl } = getAuthUrls();
    window.location.href = loginUrl;
  }, []);

  const logout = useCallback(() => {
    const { logoutUrl } = getAuthUrls();
    // Clear impersonation token on logout
    clearImpersonationToken();
    window.location.href = logoutUrl;
  }, []);

  const startImpersonation = useCallback(async (targetEmployeeId, reason) => {
    setImpersonationLoading(true);
    try {
      const response = await impersonationApi.startImpersonation(targetEmployeeId, reason);
      
      if (!response.sessionToken) {
        throw new Error('No session token received from impersonation API');
      }
      
      setImpersonationToken(response.sessionToken);
      
      // Refresh user data to get the impersonated user's data
      const employeeData = await employeesApi.getMeAuth();
      
      const userWithEmployee = {
        employee: employeeData
      };
      setUser(userWithEmployee);
      
      setImpersonationSession({
        active: true,
        targetEmployee: response.targetEmployee,
        sessionToken: response.sessionToken,
        expiresAt: response.expiresAt,
        originalUser: user // Store the original user
      });
      
      // Trigger a refresh of components that depend on user data
      setImpersonationChangeKey(prev => prev + 1);
      
      return response;
    } catch (error) {
      console.error('Failed to start impersonation:', error);
      throw error;
    } finally {
      setImpersonationLoading(false);
    }
  }, [user]);

  const endImpersonation = useCallback(async () => {
    setImpersonationLoading(true);
    try {
      await impersonationApi.endImpersonation();
      clearImpersonationToken();
      
      // Refresh user data to get the original user's data back
      const employeeData = await employeesApi.getMeAuth();
      const userWithEmployee = {
        employee: employeeData
      };
      setUser(userWithEmployee);
      
      setImpersonationSession(null);
      
      // Trigger a refresh of components that depend on user data
      setImpersonationChangeKey(prev => prev + 1);
    } catch (error) {
      console.error('Failed to end impersonation:', error);
      throw error;
    } finally {
      setImpersonationLoading(false);
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const employeeData = await employeesApi.getMeAuth();
        const userWithEmployee = {
          employee: employeeData
        };
      
        setUser(userWithEmployee);
        
        // Check if there's an active impersonation session
        try {
          const status = await impersonationApi.getStatus();
          if (status.active) {
            setImpersonationToken(status.sessionToken);
            setImpersonationSession({
              active: true,
              targetEmployee: status.targetEmployee,
              sessionToken: status.sessionToken,
              expiresAt: status.expiresAt,
              originalUser: status.impersonator,
              reason: status.reason,
              startedAt: status.startedAt
            });
          }
        } catch (error) {
          // Ignore error if impersonation status check fails
          console.debug('No active impersonation session');
        }

      } catch (error) {
        setUser(null);
      }

      setAuthInProgress(false);

    }

    checkAuth();

  }, []);

  const value = React.useMemo(() => ({
    user,
    login,
    logout,
    authInProgress,
    isAuthenticated: !!user,
    impersonationSession,
    impersonationLoading,
    startImpersonation,
    endImpersonation,
    isImpersonating: !!impersonationSession?.active,
    impersonationChangeKey, // Components can use this to trigger re-renders
  }), [user, login, logout, authInProgress, impersonationSession, impersonationLoading, startImpersonation, endImpersonation, impersonationChangeKey]);

  return (
    <AuthContext.Provider value={value}>
      {authInProgress && <LoadingSpinner message="Authenticating..." />}
      {children}
    </AuthContext.Provider>
  );
};

// Changed from arrow function to named function declaration for HMR compatibility
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}